import React, {
  useState,
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from "react";
import { HiXMark } from "react-icons/hi2";
import {
  HiArrowLeft,
  HiArrowRight,
  HiArrowPath,
  HiExclamationTriangle,
  HiMoon,
  HiOutlineQuestionMarkCircle,
  HiPlus,
} from "react-icons/hi2";
import wellnessBG from "../../assets/wellnesslogBG.jpg";
import { fetchQuestions } from "../../services/questionsService";
import {
  getDayPostReferences,
  generateDateID,
  getHealthLogs,
} from "../../services/dayPostService";
import { useAuth } from "../../contexts/AuthContext";
import { HealthLog } from "../../types/post";
import Loading from "../common/loading";
import {
  FormCheckRow,
  FormDisclosureLink,
} from "./training-log/PainInjurySection";
import { calcSleepFromTimeline, formatSleepDuration } from "../../utils";
import { calcSleepFromHealthLogs } from "../../utils";
import { userService } from "../../services/userService";

interface Question {
  question: string;
  answers: string[];
  title?: string | null;
  video?: string | null;
}

interface AddWellnessLogFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (wellnessLog: {
    health: number;
    mood: number;
    physical: number;
    recovery: number;
    sick: boolean;
    sleep: number;
    sleepTime: number;
    stress: number;
    wellnessScore: number;
    soreness: number;
    injured: boolean;
    trainingCapacity?: "full" | "modified" | "none";
  }) => void;
  dateID: string;
  editMode?: boolean;
  initialData?: {
    health: number;
    mood: number;
    physical: number;
    recovery: number;
    sick: boolean;
    sleep: number;
    sleepTime: number;
    stress: number;
    wellnessScore: number;
    soreness: number;
    injured: boolean;
    trainingCapacity?: "full" | "modified" | "none";
  };
  logId?: string;
  transparent?: boolean; // New prop for transparent/embedded mode
  trainingWarning?: boolean; // New prop for training warning
  onRegenerateQuestions?: () => void; // Callback when questions are regenerated
  onGoBackToPreviousDay?: () => void; // Switches the host page's selected date to the previous day
}

const AddWellnessLogForm: React.FC<AddWellnessLogFormProps> = ({
  open,
  onClose,
  onSubmit,
  dateID,
  editMode = false,
  initialData,
  logId,
  transparent = false, // Default to false for backwards compatibility
  trainingWarning = true, // Default to true for backwards compatibility
  onRegenerateQuestions,
  onGoBackToPreviousDay,
}) => {
  const { currentUser, userData } = useAuth();

  // Health data states
  const [healthData, setHealthData] = useState<
    (HealthLog & { id: string }) | null
  >(null);
  const [loadingHealthData, setLoadingHealthData] = useState(false);
  const [healthDataChecked, setHealthDataChecked] = useState(false);

  // Slider states
  const [sliders, setSliders] = useState<Record<string, number>>({
    mood: initialData?.mood || 3,
    recovery: initialData?.recovery || 3,
    sleep: initialData?.sleep || 3,
    stress: initialData?.stress || 3,
    health: initialData?.health || 3,
    physical: initialData?.physical || 3,
  });

  const [sleepTime, setSleepTime] = useState(initialData?.sleepTime || 3);
  const [sick, setSick] = useState(initialData?.sick || false);
  const [injured, setInjured] = useState(initialData?.injured || false);
  // Unwell / injury card; opens on its own when either is already set.
  const [showUnwellSection, setShowUnwellSection] = useState(
    !!(initialData?.sick || initialData?.injured),
  );
  const [trainingCapacity, setTrainingCapacity] = useState<
    "full" | "modified" | "none"
  >(initialData?.trainingCapacity || "full");
  const [soreness, setSoreness] = useState(initialData?.soreness || 0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [questions, setQuestions] = useState<Record<string, Question>>({});
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [showVideo, setShowVideo] = useState<string | null>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [footerHasBeenShown, setFooterHasBeenShown] = useState(false);

  // Training check states
  const [showTrainingDialog, setShowTrainingDialog] = useState(false);
  const [checkingTraining, setCheckingTraining] = useState(false);
  const [trainingCheckComplete, setTrainingCheckComplete] = useState(false);

  // Wellness explanation dialog state
  const [showWellnessExplanation, setShowWellnessExplanation] = useState(false);

  // Simple mode toggle state
  const [simpleMode, setSimpleMode] = useState(
    userData?.simpleWellnessSetting || false,
  );

  const categories = useMemo(
    () => ["mood", "recovery", "sleep", "stress", "health", "physical"],
    [],
  );

  // Sleep time responses mapping
  const sleepTimeResponses: Record<number, string> = {
    1: "less than 4 hours",
    2: "4 to 5 hours",
    3: "5 to 6 hours",
    4: "6 to 6.5 hours",
    5: "6.5 to 7.5 hours",
    6: "7.5 to 8.5 hours",
    7: "8.5+ hours",
  };
  // soreness scale responses mapping
  const sorenessScale: Record<number, string> = {
    0: "No soreness.",
    1: "Barely noticeable tightness.",
    2: "Mild discomfort, no impact on movement.",
    3: "Slight soreness during specific movements.",
    4: "Noticeable soreness, but doesn't hinder performance.",
    5: "Moderate soreness, mildly affects some movement.",
    6: "Sore with discomfort during moderate activity.",
    7: "Significant soreness, alters movement patterns.",
    8: "Very sore, limits range of motion.",
    9: "Severe soreness, difficult to move without pain.",
    10: "Extremely sore and movement limiting.",
  };

  // Utility function to get previous day's dateID
  const getPreviousDayDateID = (currentDateID: string): string => {
    const year = parseInt(currentDateID.substring(0, 4));
    const month = parseInt(currentDateID.substring(4, 6));
    const day = parseInt(currentDateID.substring(6, 8));

    const currentDate = new Date(year, month - 1, day);
    const previousDate = new Date(currentDate);
    previousDate.setDate(currentDate.getDate() - 1);

    return generateDateID(previousDate);
  };

  // Check if previous day has training logs
  const checkPreviousTraining = async () => {
    // Don't check if user is not authenticated
    if (!currentUser?.uid) {
      setTrainingCheckComplete(true);
      return true;
    }

    try {
      setCheckingTraining(true);
      const previousDateID = getPreviousDayDateID(dateID);

      // Get day post references for previous day
      const previousDayReferences = await getDayPostReferences(
        currentUser.uid,
        previousDateID,
      );

      // Check if there are any training logs
      const hasTrainingLogs =
        previousDayReferences?.trainingLogIDs &&
        previousDayReferences.trainingLogIDs.length > 0;

      if (!hasTrainingLogs && !editMode) {
        setShowTrainingDialog(true);
        return false;
      }

      setTrainingCheckComplete(true);
      return true;
    } catch (error) {
      console.error("Error checking previous training:", error);
      // On error, allow user to continue (don't block wellness logging)
      setTrainingCheckComplete(true);
      return true;
    } finally {
      setCheckingTraining(false);
    }
  };

  // Fetch health data for the current day
  const fetchHealthData = async () => {
    if (!currentUser?.uid || healthDataChecked) {
      return;
    }

    try {
      setLoadingHealthData(true);
      console.log("[WELLNESS_HEALTH] Fetching health data for dateID:", dateID);

      // Get day post references for current day
      const dayReferences = await getDayPostReferences(currentUser.uid, dateID);

      if (
        dayReferences?.healthLogIDs &&
        dayReferences.healthLogIDs.length > 0
      ) {
        // Get health logs
        const healthLogs = await getHealthLogs(
          currentUser.uid,
          dayReferences.healthLogIDs,
        );

        if (healthLogs.length > 0) {
          // Get the most recent health log (last in array)
          const latestHealthLog = healthLogs[healthLogs.length - 1];
          setHealthData(latestHealthLog);
          console.log("[WELLNESS_HEALTH] Found health data:", latestHealthLog);

          // Pre-populate sleep slider based on sleep duration
          if (latestHealthLog.sleepDuration && !editMode) {
            const durationInSeconds = calcSleepFromHealthLogs([latestHealthLog]);
            const durationHours = durationInSeconds / 3600; // Convert seconds to hours
            let sleepTimeValue = 3; // Default

            if (durationHours < 4) sleepTimeValue = 1;
            else if (durationHours < 5) sleepTimeValue = 2;
            else if (durationHours < 6) sleepTimeValue = 3;
            else if (durationHours < 6.5) sleepTimeValue = 4;
            else if (durationHours < 7.5) sleepTimeValue = 5;
            else if (durationHours < 8.5) sleepTimeValue = 6;
            else sleepTimeValue = 7;

            setSleepTime(sleepTimeValue);
            console.log(
              "[WELLNESS_HEALTH] Pre-populated sleep time:",
              sleepTimeValue,
              "from duration:",
              durationHours,
            );
          }
        } else {
          console.log("[WELLNESS_HEALTH] No health logs found in references");
          setHealthData(null);
        }
      } else {
        console.log("[WELLNESS_HEALTH] No health log IDs found for this day");
        setHealthData(null);
      }

      setHealthDataChecked(true);
    } catch (error) {
      console.error("[WELLNESS_HEALTH] Error fetching health data:", error);
      setHealthData(null);
      setHealthDataChecked(true);
    } finally {
      setLoadingHealthData(false);
    }
  };

  // Fetch questions on component mount and check training
  useEffect(() => {
    const loadQuestions = async () => {
      try {
        setLoadingQuestions(true);
        const fetchedQuestions = await fetchQuestions(categories);
        setQuestions(fetchedQuestions);
      } catch (error) {
        console.error("Error fetching questions:", error);
      } finally {
        setLoadingQuestions(false);
      }
    };

    const initializeForm = async () => {
      if (open) {
        // Load questions first
        await loadQuestions();

        // Only check training for new wellness logs (not edits) and when training warning is enabled
        if (!editMode && !trainingCheckComplete && trainingWarning) {
          await checkPreviousTraining();
        } else {
          setTrainingCheckComplete(true);
        }

        // Fetch health data after other checks are complete
        await fetchHealthData();
      }
    };

    initializeForm();
  }, [
    open,
    categories,
    editMode,
    trainingCheckComplete,
    currentUser,
    trainingWarning,
    dateID,
  ]);

  // Regenerate question for a specific category
  const regenerateCategoryQuestion = async (category: string) => {
    try {
      const fetchedQuestions = await fetchQuestions([category]);
      setQuestions((prev) => ({ ...prev, ...fetchedQuestions }));
    } catch (error) {
      console.error(`Error regenerating ${category} question:`, error);
    }
  };

  // Regenerate all wellness questions
  const regenerateAllQuestions = async () => {
    try {
      setLoadingQuestions(true);
      const fetchedQuestions = await fetchQuestions(categories);
      setQuestions(fetchedQuestions);
      onRegenerateQuestions?.(); // Notify parent component
    } catch (error) {
      console.error("Error regenerating questions:", error);
    } finally {
      setLoadingQuestions(false);
    }
  };

  // Pre-populate fields when editing an existing wellness log
  useEffect(() => {
    if (open && editMode && initialData) {
      setSliders({
        mood: initialData.mood ?? 3,
        recovery: initialData.recovery ?? 3,
        sleep: initialData.sleep ?? 3,
        stress: initialData.stress ?? 3,
        health: initialData.health ?? 3,
        physical: initialData.physical ?? 3,
      });
      setSleepTime(initialData.sleepTime ?? 3);
      setSick(initialData.sick ?? false);
      setInjured(initialData.injured ?? false);
      setShowUnwellSection(!!(initialData.sick || initialData.injured));
      setTrainingCapacity(initialData.trainingCapacity ?? "full");
      setSoreness(initialData.soreness ?? 0);
    }
  }, [open, editMode, initialData]);

  // Sync simpleMode with userData setting
  useEffect(() => {
    if (userData?.simpleWellnessSetting !== undefined) {
      setSimpleMode(userData.simpleWellnessSetting);
    }
  }, [userData?.simpleWellnessSetting]);

  // Handle simple mode - show footer immediately
  useEffect(() => {
    if (simpleMode) {
      setFooterHasBeenShown(true);
    }
  }, [simpleMode]);

  // Save simple mode setting to user profile
  const handleSimpleModeToggle = async (enabled: boolean) => {
    setSimpleMode(enabled);

    // Save to database
    if (currentUser?.uid) {
      try {
        await userService.updateUserProfile(currentUser.uid, {
          simpleWellnessSetting: enabled,
        });
        console.log("Simple wellness setting saved:", enabled);
      } catch (error) {
        console.error("Error saving simple wellness setting:", error);
      }
    }
  };

  // Calculate wellness score
  const calculateWellnessScore = () => {
    // Convert slider value (1-7) to percentage
    const convertToPercentage = (value: number): number => {
      const mapping: { [key: number]: number } = {
        7: 100,
        6: 90,
        5: 80,
        4: 65,
        3: 50,
        2: 35,
        1: 20,
      };
      return mapping[value] || 0;
    };

    // Convert sleepTime (0-7) to percentage (same scale)
    const sleepTimePercent = convertToPercentage(sleepTime);

    // Convert each wellness metric to percentage
    const physicalPercent = convertToPercentage(sliders.physical);
    const moodPercent = convertToPercentage(sliders.mood);
    const recoveryPercent = convertToPercentage(sliders.recovery);
    const sleepPercent = convertToPercentage(sliders.sleep);
    const stressPercent = convertToPercentage(sliders.stress);
    const healthPercent = convertToPercentage(sliders.health);

    // Calculate average with recovery counted 2x and sleep duration counted 1.5x
    // Total weights: physical(1) + mood(1) + recovery(2) + sleep(1) + stress(1) + health(1) + sleepTime(1.5) = 8.5
    const totalScore =
      physicalPercent +
      moodPercent +
      2 * recoveryPercent +
      sleepPercent +
      stressPercent +
      healthPercent +
      1.5 * sleepTimePercent;

    return Math.floor(totalScore / 8.5);
  };

  const handleSliderChange = (category: string, value: number) => {
    setSliders((prev) => ({ ...prev, [category]: value }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const wellnessScore = calculateWellnessScore();
      const wellnessData: any = {
        health: sliders.health,
        mood: sliders.mood,
        physical: sliders.physical,
        recovery: sliders.recovery,
        sick,
        sleep: sliders.sleep,
        sleepTime,
        stress: sliders.stress,
        wellnessScore,
        soreness,
        injured,
      };

      // Only include trainingCapacity if the user is injured
      if (injured) {
        wellnessData.trainingCapacity = trainingCapacity;
      }

      await onSubmit(wellnessData);
    } catch (error) {
      console.error("Error submitting wellness log:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset form if needed
    setShowVideo(null);
    setShowTrainingDialog(false);
    setTrainingCheckComplete(false);
    setHasScrolledToBottom(false);
    setInjured(false);
    setShowUnwellSection(false);
    setTrainingCapacity("full");
    // Reset health data states
    setHealthData(null);
    setHealthDataChecked(false);
    setLoadingHealthData(false);
    onClose();
  };

  const handleContinueWithoutTraining = () => {
    setShowTrainingDialog(false);
    setTrainingCheckComplete(true);
  };

  const handleGoBackToLog = () => {
    setShowTrainingDialog(false);
    // Switch the host page's selected date to yesterday so the user lands
    // on the day that's missing training logs, not just wherever they were.
    onGoBackToPreviousDay?.();
    handleClose();
  };

  const capitalizeFirstLetter = (str: string) => {
    if (!str) return "";
    if (str === "stress") return "Stress Management";
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  if (!open) return null;

  const formatDate = (dateID: string) => {
    const year = parseInt(dateID.substring(0, 4));
    const month = parseInt(dateID.substring(4, 6));
    const day = parseInt(dateID.substring(6, 8));
    const date = new Date(year, month - 1, day);

    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const getSliderBackground = (value: number): string => {
    const percent = ((value - 1) / 6) * 100; // converts 1–7 to 0–100%

    const colorScale = [
      "#dc2626", // 1 - red (worst)
      "#f87171",
      "#fca5a5",
      "#fde68a", // midpoint - yellow
      "#86efac",
      "#4ade80",
      "#22c55e", // 7 - green (best)
    ];

    const color = colorScale[value - 1] || "#22c55e";

    return `linear-gradient(to right, ${color} 0%, ${color} ${percent}%, #e5e7eb ${percent}%, #e5e7eb 100%)`;
  };

  const getSorenessBackground = (value: number): string => {
    const percent = (value / 10) * 100; // converts 0–10 to 0–100%

    const colorScale = [
      "#22c55e", // 0 - green
      "#34d399",
      "#6ee7b7",
      "#a7f3d0",
      "#d9f99d",
      "#fde68a", // 5 - yellowish
      "#fca5a5",
      "#fb7185",
      "#f87171",
      "#ef4444",
      "#dc2626", // 10 - red
    ];

    const color = colorScale[value] || "#22c55e";

    return `linear-gradient(to right, ${color} 0%, ${color} ${percent}%, #e5e7eb ${percent}%, #e5e7eb 100%)`;
  };

  // Training check dialog
  if (showTrainingDialog && trainingWarning) {
    const previousDateID = getPreviousDayDateID(dateID);
    const previousDate = formatDate(previousDateID);

    return (
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black backdrop-blur-lg bg-opacity-75"
        style={{ transform: "translateZ(0)" }}
      >
        <div className="bg-base-100 rounded-lg p-8 max-w-md mx-4 shadow-xl">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-xl font-nol font-grotesk">
              Training Data Missing
            </h3>
          </div>

          <div className="mb-6">
            <p className="text-base-content/80 mb-4 font-grotesk text-lg">
              We couldn't find any training logs for yesterday{" "}
              <strong>({previousDate})</strong>.
            </p>
            <p className="text-base-content/80 mb-4">
              For the most accurate Peak Readiness Score calculation, it's
              recommended to log your training data before completing your
              Wellness Log.
            </p>
          </div>

          <div className="flex flex-col gap-6">
            <button
              onClick={handleGoBackToLog}
              className="bg-base-200 py-3 rounded-md font-bold text-sm  border-2 border-base-300 font-grotesk flex items-center justify-center gap-2"
            >
              <HiArrowLeft className="w-5 h-5" />
              Go back & log yesterday's training
            </button>
            <button
              onClick={handleContinueWithoutTraining}
              className="bg-green-500/20 py-3 rounded-md font-bold text-sm border-2 border-green-500/40 font-grotesk flex items-center justify-center gap-2"
            >
              I had a rest day, continue to Wellness Log
              <HiArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while checking training, loading questions, or loading health data
  if (
    checkingTraining ||
    (loadingQuestions && !trainingCheckComplete) ||
    loadingHealthData
  ) {
    return (
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-lg"
        style={{ transform: "translateZ(0)" }}
      >
        <div className=" p-8 max-w-md mx-4  text-center">
          <div className="loading loading-spinner loading-lg mb-4 text-white"></div>
          <p className="text-white font-grotesk">
            {checkingTraining
              ? "Checking training data..."
              : loadingHealthData
                ? "Loading health data..."
                : "Loading wellness questions..."}
          </p>
        </div>
      </div>
    );
  }

  // Don't show main form until training check is complete
  if (!trainingCheckComplete) {
    return null;
  }

  // Transparent mode for embedding (like in onboarding)
  if (transparent) {
    return (
      <>
        {/* Show Loading overlay when submitting in transparent mode */}
        {isSubmitting && <Loading isLoading={isSubmitting} />}
        <div className="w-full">
          {/* Header - simplified for embedded mode */}
          <div className="flex items-center justify-between p-4 mb-4">
            <div className="text-left">
              <h1 className="text-lg font-semibold">
                Wellness <span className="font-grotesk">Check In</span>
              </h1>
              <p className="text-sm text-base-content/60 font-outfit font-grotesk">
                {formatDate(dateID)}
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="w-full">
            {loadingQuestions ? (
              <div className="flex justify-center p-8">
                <div className="loading loading-spinner loading-lg"></div>
              </div>
            ) : (
              <>
                {/* Sleep Time Section */}
                <div className="mb-4">
                  <div
                    className="card  mb-3 relative overflow-hidden"
                    style={{
                      backgroundImage: `url(${wellnessBG})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-b from-green-700/80 via-transparent to-base-100"></div>
                    <div className="relative z-10 p-4">
                      <div className="flex items-center justify-between mb-1">
                        <p
                          className={`${
                            simpleMode ? "text-md" : "text-sm"
                          } text-white font-medium font-grotesk`}
                        >
                          Sleep Time
                        </p>
                        {healthData && (
                          <div className="flex items-center gap-1">
                            <HiMoon className="w-3 h-3 text-blue-500" />
                            <span className="text-xs text-blue-600 font-medium">
                              Health Data
                            </span>
                          </div>
                        )}
                      </div>
                      {!simpleMode && (
                        <h3 className="text-lg font-semibold mb-3 leading-relaxed font-outfit text-white">
                          How long did you sleep for?
                        </h3>
                      )}

                      {/* Health Data Display */}
                      {healthData && (
                        <div className="mb-3 flex flex-wrap gap-2">
                          {healthData.sleepDuration && (
                            <div className="bg-base-200 text-base-content/80 px-4 py-2 rounded-full text-sm font-medium">
                              Sleep:{" "}
                              {formatSleepDuration(calcSleepFromHealthLogs([healthData]))}
                            </div>
                          )}
                          {healthData.rhr && (
                            <div className="bg-base-200 text-base-content/80 px-3 py-1.5 rounded-full text-xs font-medium">
                              RHR: {healthData.rhr} bpm
                            </div>
                          )}
                          {healthData.hrv && (
                            <div className="bg-base-200 text-base-content/80 px-3 py-1.5 rounded-full text-xs font-medium">
                              HRV: {healthData.hrv} ms
                            </div>
                          )}
                          {healthData.respRate && (
                            <div className="bg-base-200 text-base-content/80 px-3 py-1.5 rounded-full text-xs font-medium">
                              Resp: {healthData.respRate} /min
                            </div>
                          )}
                        </div>
                      )}

                      <div className="p-3 mb-3 bg-base-200 bg-opacity-20 backdrop-blur-sm text-white rounded-lg">
                        <p className="font-semibold font-grotesk">
                          {sleepTimeResponses[sleepTime] ||
                            "Select a response..."}
                        </p>
                      </div>
                      <input
                        type="range"
                        min="1"
                        max="7"
                        value={sleepTime}
                        onChange={(e) => setSleepTime(Number(e.target.value))}
                        className="custom-slider w-full"
                        step="1"
                        style={{ background: getSliderBackground(sleepTime) }}
                      />
                    </div>
                  </div>
                </div>

                {/* Category Sliders */}
                {categories.map((category) => (
                  <div key={category} className="mb-4">
                    <div className="card bg-base-100/60 backdrop-blur-sm p-4">
                      <div className="flex justify-between items-start mb-1">
                        <div>
                          <p
                            className={`${
                              simpleMode ? "text-md" : "text-sm"
                            } text-base-content/50 font-medium mb-1 font-grotesk`}
                          >
                            {capitalizeFirstLetter(category)}
                          </p>
                          {!simpleMode &&
                            category === "physical" &&
                            questions[category]?.title && (
                              <p className="text-sm text-base-content/50 font-semibold font-grotesk">
                                {questions[category].title}
                              </p>
                            )}
                        </div>
                        {!simpleMode && questions[category]?.video && (
                          <button
                            onClick={() =>
                              setShowVideo(
                                showVideo === category ? null : category,
                              )
                            }
                            className="p-2 bg-base-200/60 rounded-full hover:bg-base-200/60 transition-colors"
                          >
                            <HiOutlineQuestionMarkCircle className="w-5 h-5 text-base-content/80" />
                          </button>
                        )}
                      </div>
                      {!simpleMode && (
                        <>
                          <h3 className="text-lg font-semibold mb-3 leading-relaxed font-outfit">
                            {questions[category]?.question ||
                              "Loading question..."}
                          </h3>
                          {/* Video Section */}
                          {showVideo === category &&
                            questions[category]?.video && (
                              <div className="mb-3">
                                <iframe
                                  src={questions[category]?.video || ""}
                                  width="100%"
                                  height="200"
                                  className="rounded-lg"
                                  frameBorder="0"
                                  allow="autoplay; fullscreen; picture-in-picture"
                                  title={`Wellness question video for ${category}`}
                                />
                              </div>
                            )}
                          <div className="p-3 mb-3 bg-base-200 text-base-content rounded-lg">
                            <p className="font-semibold font-grotesk">
                              {questions[category]?.answers?.[
                                sliders[category] - 1
                              ] || "Select a response..."}
                            </p>
                          </div>
                        </>
                      )}

                      <input
                        type="range"
                        min="1"
                        max="7"
                        value={sliders[category]}
                        onChange={(e) =>
                          handleSliderChange(category, Number(e.target.value))
                        }
                        className={`w-full custom-slider`}
                        step="1"
                        style={{
                          background: getSliderBackground(sliders[category]),
                        }}
                      />
                      {/* Regenerate Question Button for all categories except sleep */}
                      {category !== "sleep" && (
                        <div className="text-right mt-8">
                          <button
                            onClick={() => regenerateCategoryQuestion(category)}
                            className="flex items-center gap-2 text-sm text-base-content/80 hover:text-base-content transition-colors font-grotesk"
                          >
                            <HiArrowPath className="w-4 h-4" />
                            Regenerate Question
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Regenerate All Questions Button */}
                <div className="text-center mt-6">
                  <button
                    onClick={regenerateAllQuestions}
                    disabled={loadingQuestions}
                    className="flex items-center justify-center gap-2 text-sm text-base-content/80 hover:text-base-content transition-colors font-grotesk mx-auto mb-4"
                  >
                    <HiArrowPath
                      className={`w-4 h-4 ${loadingQuestions ? "animate-spin" : ""}`}
                    />
                    {loadingQuestions
                      ? "Regenerating..."
                      : "Regenerate All Questions"}
                  </button>
                </div>

                {/* Submit Button */}
                <div className="text-center mt-2">
                  {isSubmitting ? (
                    <div className="p-4 bg-base-300/60 backdrop-blur-sm text-white rounded-lg">
                      <p className="mb-3 font-grotesk">
                        Calculating & logging your wellness, one moment
                      </p>
                      <div className="w-full">
                        <div className="loading loading-spinner loading-md text-white"></div>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={handleSubmit}
                      className="bg-neutral text-neutral-content font-grotesk rounded-md backdrop-blur-sm py-5 font-bold btn-lg w-full hover:bg-neutral/60"
                    >
                      Submit
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </>
    );
  }

  // Handle scroll to detect when user reaches bottom
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold
    setHasScrolledToBottom(isAtBottom);

    // Once user scrolls to bottom for the first time, keep footer visible
    if (isAtBottom && !footerHasBeenShown) {
      setFooterHasBeenShown(true);
    }
  };

  // Original modal mode
  return (
    <div
      className="fixed inset-0 z-[9999] bg-black bg-opacity-75 backdrop-blur-sm"
      style={{ transform: "translateZ(0)" }}
    >
      <Loading isLoading={isSubmitting} />
      <div className="flex flex-col h-full md:items-center md:justify-center md:p-4 overflow-y-auto">
        <div className="w-full max-w-2xl mx-auto bg-base-300 md:rounded-xl shadow-lg flex flex-col h-full md:h-auto md:max-h-[70vh] md:my-4">
          {/* Header - Fixed on mobile */}
          <div className="flex justify-between items-center p-6 md:p-6 border-b border-base-200 bg-base-100 flex-shrink-0 md:rounded-t-xl">
            <div>
              <h2 className="text-2xl text-base-content font-grotesk">
                <strong>Wellness</strong> Log
              </h2>
              <p className="text-xs text-base-content/80 mt-1 font-grotesk">
                {formatDate(dateID)}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowWellnessExplanation(true)}
                className="p-2 hover:bg-base-200 rounded-full transition-colors"
              >
                <HiOutlineQuestionMarkCircle className="w-8 h-8 text-base-content/50 " />
              </button>
            </div>
          </div>

          {/* Content - Scrollable */}
          <div
            className="flex-1 overflow-y-auto bg-base-100"
            onScroll={handleScroll}
            style={{
              WebkitOverflowScrolling: "touch",
              minHeight: 0,
            }}
          >
            {loadingQuestions ? (
              <div className="flex justify-center p-8">
                <div className="loading loading-spinner loading-lg"></div>
              </div>
            ) : (
              <>
                {/* Sleep Time Section */}
                <div className="mb-0">
                  <div
                    className="relative overflow-hidden"
                    style={{
                      backgroundImage: `url(${wellnessBG})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-b from-green-700/80 via-transparent to-base-100"></div>
                    <div className="relative z-10 p-6">
                      <div className="flex items-center justify-between mb-2">
                        <p
                          className={`${
                            simpleMode ? "text-md" : "text-sm"
                          } text-white rounded-md font-medium font-heading`}
                        >
                          Sleep Time
                        </p>
                        {healthData && (
                          <div className="flex items-center gap-1">
                            <HiMoon className="w-3 h-3 text-blue-500" />
                            <span className="text-xs text-blue-600 font-medium">
                              Health Data
                            </span>
                          </div>
                        )}
                      </div>
                      {!simpleMode && (
                        <h3 className="text-lg font-semibold mb-4 leading-relaxed font-sans text-white">
                          How long did you sleep for?
                        </h3>
                      )}

                      {/* Health Data Display */}
                      {healthData && (
                        <div className="mb-4 flex flex-wrap gap-2">
                          {healthData.sleepDuration && (
                            <div className="bg-base-200 text-base-content/80 px-4 py-2 rounded-full text-sm font-medium">
                              Sleep:{" "}
                              {formatSleepDuration(
                                calcSleepFromTimeline(
                                  healthData.sleepTimeline,
                                  healthData.sleepDuration,
                                ),
                              )}
                            </div>
                          )}
                          {healthData.rhr && (
                            <div className="bg-base-200 text-base-content/80 px-4 py-2 rounded-full text-sm font-medium">
                              RHR: {healthData.rhr} bpm
                            </div>
                          )}
                          {healthData.hrv && (
                            <div className="bg-base-200 text-base-content/80 px-4 py-2 rounded-full text-sm font-medium">
                              HRV: {healthData.hrv} ms
                            </div>
                          )}
                          {healthData.respRate && (
                            <div className="bg-base-200 text-base-content/80 px-4 py-2 rounded-full text-sm font-medium">
                              Resp: {healthData.respRate} /min
                            </div>
                          )}
                        </div>
                      )}

                      <div className="p-4 mb-4 bg-black bg-opacity-50 backdrop-blur-sm text-white rounded-lg">
                        <p className="font-semibold font-sans">
                          {sleepTimeResponses[sleepTime] ||
                            "Select a response..."}
                        </p>
                      </div>
                      <input
                        type="range"
                        min="1"
                        max="7"
                        value={sleepTime}
                        onChange={(e) => setSleepTime(Number(e.target.value))}
                        className="custom-slider w-full mb-4"
                        step="1"
                        style={{ background: getSliderBackground(sleepTime) }}
                      />
                      {/* <div className="flex justify-between px-2.5 mt-0 text-xs text-base-content/60">
                      {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                        <span key={num}>{num}</span>
                      ))}
                    </div> */}
                    </div>
                  </div>
                </div>

                {/* Category Sliders */}
                {categories.map((category) => (
                  <div key={category} className="mb-0">
                    <div className="bg-base-100 border-t border-base-300/40 rounded-lg p-6">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p
                            className={`${
                              simpleMode ? "text-md" : "text-sm"
                            } text-base-content rounded-md font-medium mb-2 font-heading`}
                          >
                            {capitalizeFirstLetter(category)}
                          </p>
                          {!simpleMode &&
                            category === "physical" &&
                            questions[category]?.title && (
                              <p className="text-sm text-base-content/50 font-semibold font-grotesk">
                                {questions[category].title}
                              </p>
                            )}
                        </div>
                        {!simpleMode && questions[category]?.video && (
                          <button
                            onClick={() =>
                              setShowVideo(
                                showVideo === category ? null : category,
                              )
                            }
                            className="p-2 bg-base-200 rounded-full hover:bg-base-200 transition-colors"
                          >
                            <HiOutlineQuestionMarkCircle className="w-5 h-5 text-base-content/50" />
                          </button>
                        )}
                      </div>
                      {!simpleMode && (
                        <>
                          <h3 className="text-lg font-semibold mb-4 leading-tight font-sans">
                            {questions[category]?.question ||
                              "Loading question..."}
                          </h3>
                          {/* Video Section */}
                          {showVideo === category &&
                            questions[category]?.video && (
                              <div className="mb-4">
                                <iframe
                                  src={questions[category]?.video || ""}
                                  width="100%"
                                  height="200"
                                  className="rounded-lg"
                                  frameBorder="0"
                                  allow="autoplay; fullscreen; picture-in-picture"
                                  title={`Wellness question video for ${category}`}
                                />
                              </div>
                            )}
                          <div className="p-4 mb-6 bg-base-200 text-base-content rounded-lg">
                            <p className="font-semibold font-sans">
                              {questions[category]?.answers?.[
                                sliders[category] - 1
                              ] || "Select a response..."}
                            </p>
                          </div>
                        </>
                      )}

                      <input
                        type="range"
                        min="1"
                        max="7"
                        value={sliders[category]}
                        onChange={(e) =>
                          handleSliderChange(category, Number(e.target.value))
                        }
                        className={`w-full mb-4 custom-slider`}
                        step="1"
                        style={{
                          background: getSliderBackground(sliders[category]),
                        }}
                      />
                      {/* <div className="flex justify-between px-2.5 mt-0 text-xs text-base-content/60">
                        {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                          <span key={num}>{num}</span>
                        ))}
                      </div> */}
                      {/* Regenerate Question Button for all categories except sleep */}
                      {category !== "sleep" && (
                        <div className="text-right mt-4">
                          <button
                            onClick={() => regenerateCategoryQuestion(category)}
                            className="flex items-center gap-2 text-sm text-base-content/50 hover:text-base-content transition-colors font-grotesk"
                          >
                            <HiArrowPath className="w-4 h-4" />
                            Regenerate Question
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Soreness Section */}
                <div className="mb-0">
                  <div className="border-t border-base-300/40 bg-base-100 rounded-lg p-6">
                    <div className="flex">
                      <p
                        className={`${
                          simpleMode ? "text-md" : "text-sm"
                        } text-base-content rounded-md font-medium mb-2 font-heading`}
                      >
                        Soreness
                      </p>
                    </div>
                    {!simpleMode && (
                      <p className="text-lg font-semibold mb-2 font-sans">
                        How sore are you today?
                      </p>
                    )}
                    {!simpleMode && (
                      <div className="p-4 mb-4 bg-base-200 text-base-content rounded-lg">
                        <p className="font-semibold font-sans">
                          {sorenessScale[soreness] || "Select a response..."}
                        </p>
                      </div>
                    )}
                    <input
                      type="range"
                      min="0"
                      max="10"
                      value={soreness}
                      onChange={(e) => setSoreness(Number(e.target.value))}
                      className="custom-slider w-full mb-4"
                      step="1"
                      style={{ background: getSorenessBackground(soreness) }}
                    />
                    {/* <div className="flex justify-between px-2.5 mt-0 text-xs text-base-content/60">
                      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                        <span key={num}>{num}</span>
                      ))}
                    </div> */}
                  </div>
                </div>

                {/* Unwell / injury — collapsed behind a muted link by default */}
                <div className="border-t border-base-300/40 px-6 pt-3 pb-6 space-y-3">
                  <FormDisclosureLink
                    title="Unwell or injured?"
                    open={showUnwellSection}
                    onOpenChange={setShowUnwellSection}
                  />
                  {showUnwellSection && (
                    <div className="rounded-[10px] border border-base-300 bg-base-100 px-4">
                      <FormCheckRow
                        title="Felt unwell in the last 24 hours"
                        checked={sick}
                        onToggle={() => setSick(!sick)}
                      />
                      <div className="h-px bg-base-300" />
                      <FormCheckRow
                        title="Currently injured"
                        checked={injured}
                        onToggle={() => setInjured(!injured)}
                      />
                      {injured && (
                        <>
                          <div className="h-px bg-base-300" />
                          <div className="pt-3 pb-4">
                            <p className="text-sm font-medium font-grotesk text-base-content/70 mb-3">
                              What training can you do?
                            </p>
                            <div className="flex flex-col gap-2">
                              {[
                                { value: "full" as const, label: "Full training" },
                                {
                                  value: "modified" as const,
                                  label: "Modified training",
                                },
                                { value: "none" as const, label: "No training" },
                              ].map((option) => (
                                <button
                                  key={option.value}
                                  type="button"
                                  onClick={() => setTrainingCapacity(option.value)}
                                  className={`flex items-center justify-between p-3 px-4 rounded-lg border transition-colors ${
                                    trainingCapacity === option.value
                                      ? "border-base-content bg-base-200"
                                      : "border-base-300 bg-base-100"
                                  }`}
                                >
                                  <span className="text-sm font-medium font-grotesk">
                                    {option.label}
                                  </span>
                                  <div
                                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                      trainingCapacity === option.value
                                        ? "border-base-content"
                                        : "border-base-300"
                                    }`}
                                  >
                                    {trainingCapacity === option.value && (
                                      <div className="w-2 h-2 rounded-full bg-base-content" />
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer - Fixed on mobile - Show once user has scrolled to bottom or in simple mode */}
          {(footerHasBeenShown || simpleMode) && (
            <div className="p-8 flex-shrink-0 relative z-50 bg-base-100 pb-12">
              <button
                onClick={handleSubmit}
                className="bg-green-600 text-white py-4 px-6 font-grotesk font-bold rounded-xl w-full disabled:bg-primary/30 disabled:text-white/50 disabled:cursor-not-allowed"
                disabled={isSubmitting || (!footerHasBeenShown && !simpleMode)}
              >
                {isSubmitting ? (
                  <>
                    <span className="loading loading-spinner loading-sm mr-2"></span>
                    Logging...
                  </>
                ) : (
                  "Log Wellness"
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Wellness Explanation Dialog */}
      {showWellnessExplanation && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-4 overflow-y-auto"
          onClick={() => setShowWellnessExplanation(false)}
        >
          <div
            className="bg-base-100 rounded-md shadow-xl max-w-md w-full p-8 my-8 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-base-content font-grotesk">
                <span className="font-light">Tracking</span> Wellness
              </h3>
              <button
                onClick={() => setShowWellnessExplanation(false)}
                className="p-1 hover:bg-base-200 rounded-full transition-colors"
              >
                <HiXMark className="w-5 h-5 text-base-content/60" />
              </button>
            </div>

            <div className="space-y-4 text-sm text-base-content/80">
              <p className="font-grotesk text-lg">
                The Wellness log is your self-reported snapshot of sleep
                quality, duration, mood, stress, recovery, physical perception
                and health.
              </p>
              <div className="bg-green-500/20 p-4 text-base-content rounded-md">
                <p className="text-base-content font-bold font-grotesk">
                  The wellness log captures your experience that no wearable can
                  measure directly.
                </p>
              </div>
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold text-base-content mb-1">
                    What we track:
                  </h4>
                  <ul className="space-y-1 ml-4">
                    <li>
                      • <strong>Mood:</strong> Your motivation, emotional state
                      and mental outlook
                    </li>
                    <li>
                      • <strong>Recovery:</strong> How well your body has
                      recovered from previous training
                    </li>
                    <li>
                      • <strong>Sleep:</strong> Quality and Duration of your
                      rest
                    </li>
                    <li>
                      • <strong>Stress Management:</strong> Mental and emotional
                      stress management
                    </li>
                    <li>
                      • <strong>Health:</strong> Perceived health and habits
                    </li>
                    <li>
                      • <strong>Physical:</strong> Perceived physical readiness
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-base-content mb-1">
                    Why it matters:
                  </h4>
                  <p>
                    By tracking these metrics daily, you can identify patterns,
                    optimise your training, and make informed decisions about
                    when to push harder, hold back and where to prioritise your
                    recovery.
                  </p>
                </div>

                <div className="bg-base-200 rounded-md p-4">
                  {/* Simple Mode Toggle */}
                  <div className="flex justify-between items-center gap-2">
                    <div>
                      <p>
                        Activate <strong>simple mode?</strong>
                      </p>
                      <small>Just answer with sliders & minimal text.</small>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={simpleMode}
                        onChange={(e) =>
                          handleSimpleModeToggle(e.target.checked)
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-neutral peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-base-100 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-base-100 after:border-base-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowWellnessExplanation(false)}
                className="px-4 py-2 bg-neutral w-full text-white rounded-lg hover:bg-green-700 transition-colors font-grotesk font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddWellnessLogForm;
